/**
 * PackLoader - Orchestrates content pack loading.
 *
 * The PackLoader:
 * 1. Maintains a manifest of known packs
 * 2. Checks entitlements before loading
 * 3. Resolves dependencies and determines load order
 * 4. Dynamically imports and registers packs
 */

import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction } from 'mobx';

import type { ContentPack, ContentPackModule, PackContext } from '../../content-packs/types';
import type { EntitlementKey } from '@idle-depths/shared';

/**
 * Pack manifest entry.
 */
interface PackManifestEntry {
  /** Pack ID */
  id: string;
  /** Dynamic import function */
  loader: () => Promise<ContentPackModule>;
  /** Required entitlement (null = always load) */
  entitlement: EntitlementKey | null;
  /** Dependencies (pack IDs) */
  dependencies: string[];
}

/**
 * Pack loading status.
 */
export type PackLoadStatus = 'pending' | 'loading' | 'loaded' | 'error' | 'skipped';

/**
 * Pack state.
 */
interface PackState {
  status: PackLoadStatus;
  error?: Error;
  pack?: ContentPack;
}

/**
 * PackLoader orchestrates content pack loading.
 *
 * @example
 * ```ts
 * const loader = new PackLoader();
 *
 * // Register packs in manifest
 * loader.registerManifest('core', () => import('./core'), null, []);
 * loader.registerManifest('premium', () => import('./premium'), 'premium_unlock', ['core']);
 *
 * // Load all packs the user has access to
 * await loader.loadAll(userEntitlements, packContext);
 *
 * // Check status
 * console.log(loader.isLoaded('core')); // true
 * console.log(loader.getStatus('premium')); // 'loaded' or 'skipped'
 * ```
 */
export class PackLoader {
  /** Pack manifest */
  private manifest = new Map<string, PackManifestEntry>();

  /** Pack states */
  private states = new Map<string, PackState>();

  /** Overall loading status */
  loading = false;

  /** Overall error (if any) */
  error: Error | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Register a pack in the manifest.
   *
   * @param id - Pack ID
   * @param loader - Dynamic import function
   * @param entitlement - Required entitlement (null = always load)
   * @param dependencies - Pack IDs that must load first
   */
  registerManifest(
    id: string,
    loader: () => Promise<ContentPackModule>,
    entitlement: EntitlementKey | null,
    dependencies: string[] = []
  ): void {
    if (this.manifest.has(id)) {
      throw new Error(`Pack "${id}" is already registered in manifest`);
    }

    this.manifest.set(id, {
      id,
      loader,
      entitlement,
      dependencies,
    });

    runInAction(() => {
      this.states.set(id, { status: 'pending' });
    });
  }

  /**
   * Load all packs the user has access to.
   *
   * @param entitlements - User's entitlements
   * @param context - Pack registration context
   */
  async loadAll(entitlements: Set<EntitlementKey>, context: PackContext): Promise<void> {
    // Skip if all packs are already loaded (HMR protection)
    const loadedPacks = this.getLoadedPacks();
    if (loadedPacks.length > 0 && loadedPacks.length === this.manifest.size) {
      return;
    }

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      // Determine which packs to load
      const packsToLoad = this.getPacksToLoad(entitlements);

      // Sort by dependencies
      const loadOrder = this.topologicalSort(packsToLoad);

      // Load each pack in order (skip already loaded)
      for (const packId of loadOrder) {
        if (!this.isLoaded(packId)) {
          await this.loadPack(packId, context);
        }
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      runInAction(() => {
        this.error = err;
      });
      Sentry.captureException(error, {
        tags: { context: 'pack_loader' },
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Get pack loading status.
   *
   * @param packId - Pack ID
   */
  getStatus(packId: string): PackLoadStatus {
    return this.states.get(packId)?.status ?? 'pending';
  }

  /**
   * Check if a pack is loaded.
   *
   * @param packId - Pack ID
   */
  isLoaded(packId: string): boolean {
    return this.getStatus(packId) === 'loaded';
  }

  /**
   * Get all loaded pack IDs.
   */
  getLoadedPacks(): string[] {
    const result: string[] = [];
    for (const [id, state] of this.states) {
      if (state.status === 'loaded') {
        result.push(id);
      }
    }
    return result;
  }

  /**
   * Get error for a pack.
   *
   * @param packId - Pack ID
   */
  getError(packId: string): Error | undefined {
    return this.states.get(packId)?.error;
  }

  /**
   * Whether the loader is currently loading.
   */
  get isLoading(): boolean {
    return this.loading;
  }

  /**
   * Reset the loader (keeps manifest, clears states).
   */
  reset(): void {
    runInAction(() => {
      for (const id of this.manifest.keys()) {
        this.states.set(id, { status: 'pending' });
      }
      this.loading = false;
      this.error = null;
    });
  }

  /**
   * Get packs that should be loaded based on entitlements.
   */
  private getPacksToLoad(entitlements: Set<EntitlementKey>): string[] {
    const result: string[] = [];

    for (const [id, entry] of this.manifest) {
      // Check if user has required entitlement
      if (entry.entitlement === null || entitlements.has(entry.entitlement)) {
        result.push(id);
      } else {
        // Mark as skipped
        runInAction(() => {
          this.states.set(id, { status: 'skipped' });
        });
      }
    }

    return result;
  }

  /**
   * Load a single pack.
   */
  private async loadPack(packId: string, context: PackContext): Promise<void> {
    const entry = this.manifest.get(packId);
    if (!entry) {
      throw new Error(`Pack "${packId}" not found in manifest`);
    }

    runInAction(() => {
      this.states.set(packId, { status: 'loading' });
    });

    try {
      // Dynamic import
      const module = await entry.loader();
      const pack = module.default;

      // Validate pack
      if (pack.id !== packId) {
        throw new Error(`Pack ID mismatch: manifest has "${packId}" but pack reports "${pack.id}"`);
      }

      // Register pack contents
      await pack.register(context);

      runInAction(() => {
        this.states.set(packId, { status: 'loaded', pack });
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      runInAction(() => {
        this.states.set(packId, { status: 'error', error: err });
      });
      throw err;
    }
  }

  /**
   * Topological sort of packs based on dependencies.
   */
  private topologicalSort(packIds: string[]): string[] {
    const result: string[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const packIdSet = new Set(packIds);

    const visit = (id: string): void => {
      if (visited.has(id)) {
        return;
      }

      if (visiting.has(id)) {
        throw new Error(`Circular dependency detected involving pack "${id}"`);
      }

      const entry = this.manifest.get(id);
      if (!entry) {
        throw new Error(`Pack "${id}" not found in manifest`);
      }

      visiting.add(id);

      // Visit dependencies first (only if they're in our load set)
      for (const depId of entry.dependencies) {
        if (packIdSet.has(depId)) {
          visit(depId);
        } else if (!this.manifest.has(depId)) {
          throw new Error(`Pack "${id}" depends on "${depId}" which is not in manifest`);
        }
        // If dependency exists but isn't being loaded (e.g., no entitlement),
        // that's an error
        else if (this.getStatus(depId) === 'skipped') {
          throw new Error(
            `Pack "${id}" depends on "${depId}" which was skipped (missing entitlement)`
          );
        }
      }

      visiting.delete(id);
      visited.add(id);
      result.push(id);
    };

    for (const id of packIds) {
      visit(id);
    }

    return result;
  }
}
